import type { IRatingMatrixFrameworkGradeScore, IRatingMatrixFrameworkRange } from '__generated__';

export function buildRangeFilter(subParameterId: number): string {
  return `subParameterID==${subParameterId}`;
}

/** Batch filter for prefetch — one GetAllDynamic for many quantitative rows. */
export function buildRangesForSubsFilter(subParameterIds: number[]): string | null {
  if (subParameterIds.length === 0) {
    return null;
  }
  if (subParameterIds.length === 1) {
    return buildRangeFilter(subParameterIds[0]);
  }
  return `(${subParameterIds.map((id) => `subParameterID==${id}`).join('||')})`;
}

export type RangeRow = {
  gradeScoreId: number;
  gradeLabel: string;
  numericScore: number;
  rangeId: number | null;
  boundaryValue: number | null;
  rangeDisplay: string;
};

export function composeRangesDialogTitle(subParameterTitle: string): string {
  return `עריכת טווחים · ${subParameterTitle}`;
}

export function composeRangesDialogSubtitle(
  measurementTypeLabel: string,
  unit: string,
  directionLabel: string,
): string {
  const unitPart = unit ? ` · יחידה ${unit}` : '';
  return `${measurementTypeLabel}${unitPart} · כיוון: ${directionLabel}`;
}

export function formatBoundaryValue(value: number): string {
  if (Number.isInteger(value)) {
    return value.toFixed(1);
  }

  return String(parseFloat(value.toPrecision(12)));
}

function withUnit(value: string, unit: string): string {
  return `${value}${unit}`;
}

function formatRangeDisplay(
  index: number,
  prevBoundary: number | null,
  thisBoundary: number | null,
  isHigherBetter: boolean,
  unit: string,
): string {
  if (thisBoundary == null) {
    return '—';
  }

  const formatted = withUnit(formatBoundaryValue(thisBoundary), unit);

  if (isHigherBetter) {
    if (index === 0) {
      return `>${formatted}`;
    }

    if (prevBoundary != null) {
      return `${withUnit(formatBoundaryValue(prevBoundary), unit)} - ${formatted}`;
    }

    return formatted;
  }

  if (index === 0) {
    return `<${formatted}`;
  }

  if (prevBoundary != null) {
    return `${formatted} - ${withUnit(formatBoundaryValue(prevBoundary), unit)}`;
  }

  return formatted;
}

export function buildRangeRows(
  sortedGrades: IRatingMatrixFrameworkGradeScore[],
  existingRanges: IRatingMatrixFrameworkRange[],
  isHigherBetter: boolean,
  unit = '',
): RangeRow[] {
  const rangesByGrade = new Map<number, IRatingMatrixFrameworkRange>();

  existingRanges.forEach((range) => {
    rangesByGrade.set(range.gradeScoreID, range);
  });

  return sortedGrades.map((grade, index) => {
    const existing = rangesByGrade.get(grade.id);
    const boundaryValue = existing?.boundaryValue ?? null;

    const prevBoundary =
      index > 0 ? (rangesByGrade.get(sortedGrades[index - 1].id)?.boundaryValue ?? null) : null;

    const rangeDisplay = formatRangeDisplay(
      index,
      prevBoundary,
      boundaryValue,
      isHigherBetter,
      unit,
    );

    return {
      gradeScoreId: grade.id,
      gradeLabel: grade.gradeLabel ?? '',
      numericScore: grade.numericScore,
      rangeId: existing?.id ?? null,
      boundaryValue,
      rangeDisplay,
    };
  });
}

export function buildRangeSaveBulkPayload(
  subParameterId: number,
  rows: RangeRow[],
): Partial<IRatingMatrixFrameworkRange>[] {
  return rows.map((row) => ({
    id: row.rangeId ?? undefined,
    subParameterID: subParameterId,
    gradeScoreID: row.gradeScoreId,
    boundaryValue: row.boundaryValue,
  }));
}

export function recomputeRangeDisplays(
  rows: RangeRow[],
  isHigherBetter: boolean,
  unit = '',
): RangeRow[] {
  return rows.map((row, index) => {
    const prevBoundary = index > 0 ? rows[index - 1].boundaryValue : null;

    return {
      ...row,
      rangeDisplay: formatRangeDisplay(
        index,
        prevBoundary,
        row.boundaryValue,
        isHigherBetter,
        unit,
      ),
    };
  });
}
